import { FormCard, MessageTile } from "@suralink/samurai/ui";
import { FormControl, Button } from "@material-ui/core";
import { Error } from "@suralink/samurai/icons";
import { TextInput } from "@suralink/samurai/components";
import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import styles from "../styles/SSOLogin.module.scss";
import MessageChatSquare from "../icons/MessageChatSqaure";
import { getQueryParamsFromUrl, supportChat } from "../utils/helpers";
import { useFlag } from "../utils/gitlab-feature-flags";
import { FeatureFlags } from "../enums/featureFlags";
import { useTheme, ThemeProvider } from "@material-ui/core/styles";
import { suraTheme } from "@suralink/samurai/themes";

interface ComponentProps {}

type Props = ComponentProps & RouteComponentProps;

const SSOLogin: React.FC<Props> = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [emailMissing, setEmailMissing] = useState(false);
  const [passwordMissing, setPasswordMissing] = useState(false);
  const [passwordError] = useState("");

  const theme: any = useTheme();

  const queryParams = getQueryParamsFromUrl(window.location.href);
  const { coBrandingEnabled, clientId, redirectUri } = queryParams;

  const flagZendesk = useFlag(FeatureFlags.ZendeskChat);

  window.clientId = clientId;
  window.redirectUri = redirectUri;

  // if critical url params are missing send them to suralink.com
  useEffect(() => {
    if (!clientId || !redirectUri) {
      window.location.href = "https://www.suralink.com";
    }
    return;
  }, [clientId, redirectUri]);

  async function login(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    const formValid = checkForm();
    if (!formValid) return;
    // pass email to next section of login
    setEmailValid(true);
  }

  function onChangeClick() {
    setEmailValid(false);
  }

  function onForgotClick() {
    // placeholder for forgot logic and redirect
  }

  const credentialInputIsValid = typeof email === "string" && email.length > 0;

  const checkForm = () => {
    if (!credentialInputIsValid) {
      setEmailMissing(true);
      return false;
    }
    return true;
  };

  const handleSignIn = async ({ e }: { e: any }) => {
    try {
      await login(e);
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  const handleOpenChat = () => {
    supportChat(flagZendesk);
  };
  const SuraLogo = (
    <svg
      width="209"
      height="47"
      viewBox="0 0 300 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2837_52132)">
        <path
          d="M67.2514 0H0V31.6139V34.3801H21.2741V21.3668H67.2514V0Z"
          fill="#0E5AE1"
        />
        <path
          d="M0 69.6436H67.2514V37.0693V35.291H46.0038V48.3098H0V69.6436Z"
          fill="#0E5AE1"
        />
        <path
          d="M90.7963 40.567C93.6424 43.0862 97.2199 44.5516 100.962 44.7273C104.311 44.7273 105.891 43.5308 105.891 41.6372C105.891 39.4967 103.336 38.7393 99.9865 38.0423C94.9356 37.0324 88.4219 35.781 88.4219 28.5252C88.4219 23.4208 92.7414 18.9422 100.474 18.9422C104.772 18.8818 108.959 20.3528 112.341 23.1025L109.235 28.5855C106.887 26.2804 103.765 25.0125 100.532 25.0564C97.734 25.0564 95.9055 26.1267 95.9055 27.894C95.9055 29.782 98.2163 30.4791 101.508 31.1706C106.558 32.1805 113.316 33.5691 113.316 41.1323C113.316 46.6812 108.753 50.9019 100.596 50.9019C95.8048 51.0281 91.1302 49.3486 87.4467 46.1762L90.7963 40.567Z"
          fill="black"
        />
        <path
          d="M137.896 46.3021C135.241 49.2933 131.473 50.9673 127.551 50.9015C121.095 50.9015 118.053 47.2461 118.053 41.3185V19.6992H125.786V38.1681C125.786 42.3943 127.916 43.7774 131.202 43.7774C133.826 43.7335 136.29 42.4601 137.896 40.3086V19.6992H145.629V50.144H137.896V46.3021Z"
          fill="black"
        />
        <path
          d="M152.324 19.6984H160.051V23.8587C162.346 20.9059 165.765 19.1386 169.422 19.0068V26.8225C165.966 25.9992 162.351 27.2396 160.051 30.0388V50.1487H152.318L152.324 19.6984Z"
          fill="black"
        />
        <path
          d="M193.653 46.2359C191.496 49.1722 188.141 50.8956 184.585 50.9011C177.159 50.9011 171.557 45.0997 171.557 34.9515C171.557 24.9898 177.096 18.9415 184.585 18.9415C188.146 18.9305 191.512 20.6594 193.653 23.6067V19.6989H201.444V50.1492H193.653V46.2359ZM193.653 29.5947C192.068 27.4157 189.588 26.1369 186.959 26.1259C182.576 26.1259 179.534 29.655 179.534 34.9515C179.534 40.182 182.576 43.777 186.959 43.777C189.593 43.7715 192.068 42.4872 193.653 40.3083V29.5947Z"
          fill="black"
        />
        <path
          d="M208.181 8.10156H215.914V50.1436H208.181V8.10156Z"
          fill="black"
        />
        <path
          d="M221.812 10.4341C221.812 7.78859 223.884 5.64258 226.439 5.64258C228.993 5.64258 231.066 7.78859 231.066 10.4341C231.066 13.0795 228.993 15.2255 226.439 15.2255C223.895 15.2036 221.833 13.0685 221.812 10.4341ZM222.607 19.6987H230.339V50.1435H222.607V19.6987Z"
          fill="black"
        />
        <path
          d="M256.877 31.7403C256.877 27.5142 254.746 26.0652 251.46 26.0652C248.805 26.0926 246.319 27.4263 244.766 29.6602V50.1543H237.034V19.6985H244.766V23.6722C247.363 20.6151 251.121 18.8917 255.054 18.9466C261.504 18.9466 264.609 22.7282 264.609 28.6503V50.1598H256.877V31.7403Z"
          fill="black"
        />
        <path
          d="M282.528 38.4201L278.998 42.328V50.1436H271.265V8.10156H278.998V33.2554L290.139 19.7043H299.636L288.008 33.5079L300.002 50.1491H290.319L282.528 38.4201Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_2837_52132">
          <rect width="300" height="69.6438" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  return (
    <ThemeProvider theme={suraTheme}>
      <div
        style={{ backgroundColor: theme.palette.primary[50] }}
        className={styles.main}
      >
        <div className={styles.login}>
          <div className={styles.marketing}></div>
          <div className={styles.inputModal}>
            <FormControl>
              <FormCard
                style={{
                  height: "650px",
                  margin: 0,
                  padding: "45px",
                  border: "none",
                }}
              >
                {false && (
                  <MessageTile
                    state="error"
                    message="Suralink is experiencing issues."
                    subtext="We are investigating an issue that is preventing users from logging into our system."
                    subsubtext="Please check https://status.suralink.com for updates."
                    style={{
                      position: "relative",
                      top: `${coBrandingEnabled ? "-13px" : "-30px"}`,
                    }}
                    icon={<Error />}
                    dismissAlertCb={() => null}
                  />
                )}

                <div className={styles.loginContainer}>
                  <div className={styles.icon}>{SuraLogo}</div>
                  <hr
                    className={emailValid ? styles.topDividerEmailText : null}
                  />
                  <React.Fragment>
                    <input type="hidden" name="clientId" value={clientId!} />
                    <input
                      type="hidden"
                      name="redirectUri"
                      value={redirectUri!}
                    />
                    <div style={{ paddingTop: "0.5rem" }}>
                      {!emailValid && (
                        <TextInput
                          value={email}
                          label="Email address"
                          onChange={(e: any) => {
                            setEmail(e.target.value.trim());
                            setEmailMissing(false);
                          }}
                          id="email"
                          fullWidth
                          error={emailMissing}
                          errorMessage="Email is required"
                          autoFocus
                        />
                      )}
                      {emailValid && (
                        <div className={styles.passwordArea}>
                          <div className={styles.emailPlaceholder}>
                            <div className={styles.emailText}>{email}</div>
                            <Button
                              variant="text"
                              className={styles.changeButton}
                              onClick={onChangeClick}
                            >
                              Change
                            </Button>
                          </div>
                          <TextInput
                            value={password}
                            label="Password"
                            onChange={(e: any) => {
                              setPassword(e.target.value.trim());
                              setPasswordMissing(false);
                            }}
                            id="password"
                            fullWidth
                            error={passwordMissing}
                            errorMessage={passwordError}
                            muiChildrenProps={{
                              InputProps: {
                                type: "password",
                              },
                            }}
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      color="primary"
                      variant="contained"
                      style={{
                        width: "100%",
                        margin: "1rem auto 0 auto",
                      }}
                      onClick={(e: any) => {
                        e.preventDefault();
                        if (checkForm()) {
                          handleSignIn({ e });
                        }
                      }}
                    >
                      {emailValid ? "Sign In" : "Continue"}
                    </Button>
                    {emailValid && (
                      <div className={styles.forgotWrapper}>
                        <Button
                          variant="text"
                          className={styles.forgotButton}
                          onClick={onForgotClick}
                        >
                          Forgot your password?
                        </Button>
                      </div>
                    )}
                    <hr
                      style={{
                        margin: emailValid ? "2rem auto" : "2.5rem auto",
                        marginBottom: "2rem",
                      }}
                    />
                    <div style={{ textAlign: "center" }}>
                      <Button variant="text">Sign in with SSO</Button>
                    </div>
                    <div style={{ textAlign: "center", marginTop: "2rem" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenChat}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <MessageChatSquare />
                          Live chat support
                        </span>
                      </Button>
                    </div>
                  </React.Fragment>
                </div>
              </FormCard>
            </FormControl>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SSOLogin;
